import type { SalesOrderDeposit } from '@/lib/services/deposits/deposits.types'
import type { SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'

// Sorting/navigation read these owner fields and preserve every other field of each row.
type DepositNavigationRow = Pick<SalesOrderDeposit, 'id' | 'effective_deposit_at'> & {
  entries: Pick<SalesOrderDeposit['entries'][number], 'id'>[]
}

/** The order's deposits, while they load. */
type SalesOrderDepositMapping<TDeposit extends DepositNavigationRow> =
  | { deposits: TDeposit[] }
  | undefined

/** The deposit report search for an order, highlighting its first entry when it opens one deposit. */
// A type alias, so it fits the path helper's search params.
export type SalesOrderDepositSearch = {
  sales_order_id: number
  entry_id?: number
}

export type SalesOrderDepositAction<TDeposit extends DepositNavigationRow = SalesOrderDeposit> =
  | { label: 'View deposit'; deposit: TDeposit; search: SalesOrderDepositSearch }
  | { label: 'View deposits'; deposit: null; search: SalesOrderDepositSearch }

export function getSalesOrderDeposits<TDeposit extends DepositNavigationRow = SalesOrderDeposit>(
  mapping: SalesOrderDepositMapping<TDeposit>
): TDeposit[] {
  return [...(mapping?.deposits ?? [])]
    .filter((deposit) => deposit?.id)
    .sort((left, right) => {
      const dateComparison = String(right.effective_deposit_at ?? '').localeCompare(
        String(left.effective_deposit_at ?? '')
      )

      return dateComparison || Number(right.id) - Number(left.id)
    })
}

export function getDepositDetailSearch(
  deposit: Pick<DepositNavigationRow, 'entries'>,
  salesOrderId: SalesOrderIdInput
): SalesOrderDepositSearch {
  const firstEntry = deposit?.entries?.[0]

  return {
    sales_order_id: Number(salesOrderId),
    ...(firstEntry?.id ? { entry_id: Number(firstEntry.id) } : {}),
  }
}

export function getSalesOrderDepositAction<
  TDeposit extends DepositNavigationRow = SalesOrderDeposit,
>(
  mapping: SalesOrderDepositMapping<TDeposit>,
  salesOrderId: SalesOrderIdInput
): SalesOrderDepositAction<TDeposit> | null {
  const deposits = getSalesOrderDeposits(mapping)
  const [firstDeposit] = deposits

  if (!firstDeposit) return null

  if (deposits.length === 1) {
    return {
      label: 'View deposit',
      deposit: firstDeposit,
      search: getDepositDetailSearch(firstDeposit, salesOrderId),
    }
  }

  return {
    label: 'View deposits',
    deposit: null,
    search: { sales_order_id: Number(salesOrderId) },
  }
}
